/**
 * The mechanic registries the **How to Play** surface is generated from (FS-01 R42, FS-12 R35).
 *
 * R35 is a drift rule, not a layout rule: every explanation must be generated from the same data as
 * the mechanic it describes, and the prose must live beside that definition rather than in the UI
 * module. Power-ups already have such a definition — `POWERUPS` in `src/game/powerups.ts` — so they
 * are not restated here. The other two card sources had no definition in code at all until now: the
 * HUD element inventory existed only in FS-12 §4.2, and hazards had no family grouping anywhere.
 *
 * `content` sits below `game` in the layer order, so nothing here imports a hazard def or a
 * power-up class; kinds are named by their `HazardKind` id and resolved by the consumer.
 */
import type { HazardKind, MechanicId } from '../core/event-map';

export type { MechanicId };

// ── HUD element inventory (FS-12 §4.2) ──────────────────────────────────────

export type HudElementId =
  | 'score'
  | 'multiplier'
  | 'resonance'
  | 'overdrive'
  | 'powerup-stored'
  | 'powerup-active'
  | 'beat'
  | 'hull-hp'
  | 'sector'
  | 'speed-tier'
  | 'boss'
  | 'ranked'
  | 'distance'
  | 'feedback-border';

export interface HudElementDef {
  readonly id: HudElementId;
  readonly displayName: string;
  /** The one sentence FS-01 R42 requires. */
  readonly oneSentence: string;
  /** How it reads without colour (FS-12 R11 / Design Bible §12). Shown on the card verbatim. */
  readonly nonColourChannel: string;
  /**
   * Does this build actually render it? FS-12 §4.2 specifies fourteen; eight exist today. The
   * inventory stays the spec's fourteen so the gap is visible here rather than forgotten, and the
   * How to Play screen shows only what a player can actually see (explaining an absent readout is
   * worse than omitting it).
   */
  readonly built: boolean;
}

export const HUD_ELEMENTS: readonly HudElementDef[] = [
  {
    id: 'score',
    displayName: 'Score',
    oneSentence: 'Distance travelled and everything you earn on the way, multiplied.',
    nonColourChannel: 'Numerals',
    built: true,
  },
  {
    id: 'multiplier',
    displayName: 'Multiplier',
    oneSentence: 'Steps up every eight hazards cleared in a row, and resets to 1× the moment you are hit.',
    nonColourChannel: 'Numeral',
    built: true,
  },
  {
    id: 'resonance',
    displayName: 'Resonance meter',
    oneSentence: 'Fills as you graze, thread node centres and steer on the beat; drains while you play it safe.',
    nonColourChannel: 'Fill length and quarter marks',
    built: true,
  },
  {
    id: 'overdrive',
    displayName: 'Overdrive',
    oneSentence: 'A full meter spends itself: triple score and one speed tier more, for ten seconds.',
    nonColourChannel: 'The meter changes border style and the word OVERDRIVE appears',
    built: true,
  },
  {
    id: 'powerup-stored',
    displayName: 'Power-up slot',
    oneSentence: 'What you are carrying. Collecting another swaps it — you hold one at a time.',
    nonColourChannel: 'A distinct glyph per power-up in a dashed slot',
    built: true,
  },
  {
    id: 'powerup-active',
    displayName: 'Active power-up',
    oneSentence: 'What is running right now, and how much of it is left.',
    nonColourChannel: 'Solid rather than dashed slot, plus numerals',
    built: false,
  },
  {
    id: 'beat',
    displayName: 'Beat pips',
    oneSentence: 'Four pips, one per beat in the bar. Everything spawns on this grid, so it is the whole game muted.',
    nonColourChannel: 'Pip fill, with the downbeat marked',
    built: true,
  },
  {
    id: 'hull-hp',
    displayName: 'Hull',
    oneSentence: 'How many hits this hull has left.',
    nonColourChannel: 'Chevron count',
    built: false,
  },
  {
    id: 'sector',
    displayName: 'Sector and movement',
    oneSentence: 'Where you are in the track — each sector moves Intro → Build → Drop → Breakdown → Finale.',
    nonColourChannel: 'Text',
    built: false,
  },
  {
    id: 'speed-tier',
    displayName: 'Speed tier',
    oneSentence: 'How fast the tunnel is coming at you. It rises with intensity and never steps — it lerps.',
    nonColourChannel: 'Numeral',
    built: true,
  },
  {
    id: 'boss',
    displayName: 'Boss progress',
    oneSentence: 'The phase you are in and the bars left in it. There is no health bar: surviving is winning.',
    nonColourChannel: 'Text plus a segmented bar',
    built: true,
  },
  {
    id: 'ranked',
    displayName: 'Ranked indicator',
    oneSentence: 'Whether this run counts for a score, and if not, why.',
    nonColourChannel: 'Lock glyph shape',
    built: false,
  },
  {
    id: 'distance',
    displayName: 'Distance',
    oneSentence: 'How far down the tunnel you have flown.',
    nonColourChannel: 'Numerals',
    built: false,
  },
  {
    id: 'feedback-border',
    displayName: 'Feedback border',
    oneSentence: 'The viewport edge pulses on a graze, a hit, a shield absorb and Overdrive.',
    nonColourChannel: 'Border width and dash pattern',
    built: false,
  },
];

// ── Hazard families (FS-12 R35) ─────────────────────────────────────────────

export interface HazardFamilyDef {
  readonly id: string;
  readonly displayName: string;
  readonly oneSentence: string;
  readonly kinds: readonly HazardKind[];
}

/**
 * FS-06 defines twelve kinds and no families; R35 asks for a card per family, and twelve cards is a
 * manual rather than a page. These five group the kinds by **what they ask of you**, which is the
 * only grouping a player can act on — not by silhouette, which they can already see.
 *
 * The mapping is total and disjoint over `ALL_KINDS`, and `tests/how-to-play.test.ts` fails if a
 * kind belongs to zero families or to two. That is what stops it drifting when a thirteenth kind
 * arrives.
 */
export const HAZARD_FAMILIES: readonly HazardFamilyDef[] = [
  {
    id: 'waveform-walls',
    displayName: 'Waveform walls',
    oneSentence: 'The music’s waveform grown into the tunnel wall — read the curve and fly the gap in it.',
    kinds: ['SineRibbon', 'SawtoothComb', 'TriangleFence', 'StandingWave'],
  },
  {
    id: 'gates',
    displayName: 'Gates',
    oneSentence: 'Hard-edged and quantised: they open and shut on the beat, so the pips tell you when to be through.',
    kinds: ['SquareGate', 'BeatGate'],
  },
  {
    id: 'rings',
    displayName: 'Rings',
    oneSentence: 'Closing from the rim inward. The way out is a radius, not a bearing — move toward or away from the centre.',
    kinds: ['PulseRing', 'FeedbackLoop'],
  },
  {
    id: 'solids',
    displayName: 'Solid masses',
    oneSentence: 'They take most of the disc at once. There is exactly one gap and you need to already be near it.',
    kinds: ['SubBassSlab', 'NoiseCloud'],
  },
  {
    id: 'moving-nodes',
    displayName: 'Moving gaps',
    oneSentence: 'The gap itself travels while the hazard approaches — aim where it is going, not where it is.',
    kinds: ['HelixBraid', 'ChirpSweep'],
  },
];

// ── Teach order (FS-01 R11, as amended by §5.3) ─────────────────────────────

/**
 * The order onboarding must teach in (FS-01 R11). PT-1's §5.3 amendment inserts `polarity` and
 * `pickup` — polarity after `steer` (the middle of the tunnel is not automatically safe) and pickup
 * after `multiplier` (that object is for collecting). Shared with the onboarding script's validator
 * so the script and the requirement cannot drift apart.
 */
export const TEACH_ORDER: readonly MechanicId[] = [
  'beat',
  'hazard-arrival',
  'node',
  'steer',
  'polarity',
  'graze',
  'multiplier',
  'pickup',
  'powerup',
  'overdrive',
];
